import type { ColumnDef } from "@tanstack/react-table";
import React from "react";
import { DialogTitle } from "@radix-ui/react-dialog";
import { highlightText } from "@repo/utils";
import { TrashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { TableCombination } from "~/providers/table-provider";
import { useTable } from "~/hooks/use-table";
import { useTableManager } from "~/managers/use-table-manager";
import { DataTable } from "../miscellaneous/data-table";
import { DataTableColumnHeader } from "../miscellaneous/data-table-column-header";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader } from "../ui/dialog";

export default function TableCombinationGroup({
  groupSize,
  onClose,
}: {
  groupSize?: number;
  onClose: () => void;
}) {
  const { t } = useTranslation("common");
  const { tableCombinations } = useTable();

  const { deleteTableCombination } = useTableManager();

  const handleDelete = async (tableCombination: TableCombination) => {
    await deleteTableCombination(tableCombination.id);
  };

  const columns: ColumnDef<TableCombination>[] = [
    {
      id: "tables",
      accessorKey: "locationName",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("TableCombinations.tables")}
        />
      ),
      cell: ({ row: { original: tableCombination }, table: dataTable }) => {
        const globalFilter = dataTable.getState().globalFilter as string;

        const tableRefs = tableCombination.tables
          .map((table) => `T${table.reference}`)
          .join(" + ");
        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(`${tableRefs}`, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: "seats",
      accessorKey: "minSeats",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("TableCombinations.seats")}
        />
      ),
      cell: ({ row: { original: tableCombination }, table: dataTable }) => {
        const globalFilter = dataTable.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(
                  `${tableCombination.minSeats} - ${tableCombination.maxSeats}`,
                  globalFilter,
                ) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: "priority",
      accessorKey: "priority",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("TableCombinations.priority")}
        />
      ),
      cell: ({ row: { original: tableCombination }, table: dataTable }) => {
        const globalFilter = dataTable.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(
                  tableCombination.priority.toString(),
                  globalFilter,
                ) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: "actions",
      size: 100,
      cell: ({ row: { original: tableCombination } }) => {
        return (
          <Button
            onClick={() => {
              void handleDelete(tableCombination);
            }}
            variant="ghost"
            className="p-1 rounded-full w-8 h-8"
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        );
      },
    },
  ];

  return (
    <Dialog
      open={!!groupSize}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-xl w-full">
        <DialogHeader>
          <DialogTitle>
            {t("TableCombinations.group_size_x", {
              x: groupSize,
            })}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[80dvh] overflow-auto p-4">
          <DataTable
            defaultSorting={[{ id: t("Table.position"), desc: false }]}
            columns={columns}
            data={tableCombinations.filter(
              (tableCombination) => tableCombination.groupSize === groupSize,
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
